import React__default, { useState, useEffect, useRef, useMemo } from 'react';
import { StyleSheet, View, Platform, Animated, Image, useWindowDimensions, Dimensions, KeyboardAvoidingView } from 'react-native';
import Auth$1, { Auth } from '@aws-amplify/auth';
import { A as AppFormCodeInput } from './AppFormCodeInput-6cbd010f.esm.js';
import { useTranslation } from 'react-i18next';
import { A as AppText, a as AppIcon } from './index-495429ce.esm.js';
import { A as AppForm, a as AppSubmitButton } from './AppSubmitButton-497b965f.esm.js';
import { A as AppFormField } from './AppFormField-8e8773f8.esm.js';
import { u as useTheme } from './useTheme-e658d3b6.esm.js';
import { useToast } from 'react-native-toast-notifications';
import * as Yup from 'yup';
import { A as AppAssets } from './index-fa849583.esm.js';
import { A as AppButton } from './AppButton-df2f0562.esm.js';
import { H as HorizontalSeparator } from './index-b665de78.esm.js';
import QRCode from 'qrcode';

const useAuth = function (config, onCognitoUserAuthenticated) {
  let viewOnNotAuth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "auth";
  if (config) Auth.configure(config);
  const [currentView, setCurrentView] = useState("initializing");
  const checkAuth = async () => {
    try {
      const session = await Auth.currentSession();
      onCognitoUserAuthenticated && onCognitoUserAuthenticated(session);
      setCurrentView("mainNav");
    } catch (err) {
      console.error("user is not signed in " + err);
      setCurrentView(viewOnNotAuth);
    }
  };
  useEffect(() => {
    if (window?.electronAPI && window?.electronAPI?.handleTokens) {
      window.electronAPI.handleTokens(tokens => {
        console.log("Renderer: Received tokens from main process:", tokens);
        if (tokens) ; else {
          checkAuth();
        }
      });
    } else {
      checkAuth();
    }
  }, []);
  const getCurrentAuthenticatedUser = () => {
    return Auth.currentAuthenticatedUser();
  };
  const getCurrentSession = () => {
    return Auth.currentSession();
  };
  const signOut = async () => {
    try {
      const result = await Auth.signOut();
      console.log("Auth - user successfully signed out");
      return result;
    } catch (error) {
      console.error("Auth - error signing out: ", error);
    }
  };
  const attemptSignInWithGoogle = () => {
    return Auth.federatedSignIn({
      provider: "Google"
    });
  };
  return {
    currentView,
    setCurrentView,
    getCurrentAuthenticatedUser,
    getCurrentSession,
    signOut,
    attemptSignInWithGoogle,
    checkAuth
  };
};

var Validations = {
  loginSchema: Yup.object().shape({
    // username: Yup.string().required().label("Username"),
    email: Yup.string().required().email().label("Email"),
    password: Yup.string().required().min(8)
    // .matches(/\d/, "Password must contain at least one number")
    // .matches(
    //   /[!@#$%^&*(),.?":{}|<>]/,
    //   "Password must contain at least one special character"
    // )
    .label("Password"),
    referralCode: Yup.string().trim().max(50).label("Referral Code")
  }),
  ssoSchema: Yup.object().shape({
    email: Yup.string().required().email().label("Email")
  }),
  forgotPasswordStage0: Yup.object().shape({
    email: Yup.string().required().email().label("Email")
  }),
  forgotPasswordStage1: Yup.object().shape({
    authorizationCode: Yup.string().required().matches(/^[0-9]+$/, "Must be only digits").min(6, "Must be exactly 6 digits").max(6, "Must be exactly 6 digits").label("Auth code"),
    password: Yup.string().required().min(8).label("New Password"),
    passwordConfirmation: Yup.string().oneOf([Yup.ref("password")], "Passwords must match")
  }),
  mfaVerificationSchema: Yup.object().shape({
    mfaCode: Yup.string().required("MFA code is required").matches(/^[0-9]+$/, "Must be only digits").min(6, "Must be exactly 6 digits").max(6, "Must be exactly 6 digits").label("MFA Code")
  }),
  totpSetupSchema: Yup.object().shape({
    totpCode: Yup.string().required("TOTP code is required").matches(/^[0-9]+$/, "Must be only digits").min(6, "Must be exactly 6 digits").max(6, "Must be exactly 6 digits").label("TOTP Code")
  }),
  mfaPhoneSchema: Yup.object().shape({
    phoneNumber: Yup.string().required("Phone number is required").matches(/^\+[1-9]\d{1,14}$/, "Phone number must be in international format (e.g., +1234567890)").label("Phone Number")
  }),
  mfaCodeVerificationSchema: Yup.object().shape({
    verificationCode: Yup.string().required("Verification code is required").matches(/^[0-9]+$/, "Must be only digits").min(6, "Must be exactly 6 digits").max(6, "Must be exactly 6 digits").label("Verification Code")
  })
};

const ForgotPassword = _ref => {
  let {
    toggleAuthType,
    AuthButton,
    showCode,
    user
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const [isResetRequestInProgress, setIsResetRequestInProgress] = useState(false);
  const [isResetConfirmInProgress, setIsResetConfirmInProgress] = useState(false);
  const [state, setState] = useState({
    stage: showCode ? 0 : 1,
    username: "",
    email: "",
    password: "",
    authorizationCode: showCode ? "" : 123456
  });
  const AppToasty = useToast();
  const resetPassword = async values => {
    try {
      if (isResetRequestInProgress) return AppToasty.show(t("passwordChangeRequestInProgressCaption"), {
        type: "warning"
      });
      setIsResetRequestInProgress(true);
      const result = await Auth.forgotPassword(values.email);
      setState({
        ...state,
        stage: 1,
        username: values.email
      });
      AppToasty.show(t("checkInboxCaption"), {
        type: "success"
      });
    } catch (error) {
      console.error("error: ", error);
      AppToasty.show(error.message, {
        type: "danger"
      });
    } finally {
      setIsResetRequestInProgress(false);
    }
  };
  const confirmResetPassword = values => {
    if (isResetConfirmInProgress) return AppToasty.show(t("passwordChangeCaption"), {
      type: "warning"
    });
    setIsResetConfirmInProgress(true);
    const {
      username
    } = state;
    const {
      password,
      authorizationCode
    } = values;
    if (showCode) {
      Auth.forgotPasswordSubmit(username, authorizationCode, password).then(() => {
        AppToasty.show(t("passwordChangedCaption"), {
          type: "success"
        });
        toggleAuthType("showSignIn");
      }).catch(err => {
        console.error("error resetting password:", err);
        AppToasty.show(t("passwordChangeFailedCaption"), {
          type: "danger"
        });
      }).finally(() => {
        setIsResetConfirmInProgress(false);
      });
    } else {
      Auth.completeNewPassword(user, password, {}).then(() => {
        AppToasty.show(t("passwordChangedCaption"), {
          type: "success"
        });
        toggleAuthType("showSignIn");
      }).catch(err => {
        console.error("error resetting password:", err);
        AppToasty.show(t("passwordChangeFailedCaption"), {
          type: "danger"
        });
      }).finally(() => {
        setIsResetConfirmInProgress(false);
      });
    }
  };
  const {
    stage
  } = state;
  return /*#__PURE__*/React__default.createElement(View, {
    style: {
      alignContent: "center"
    }
  }, stage === Number(0) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      email: ""
    },
    onSubmit: values => resetPassword(values),
    validationSchema: Validations.forgotPasswordStage0
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: styles$8.titleLabel,
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    },
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.emailRegistrationCaption")), /*#__PURE__*/React__default.createElement(AppFormField, {
    name: "email",
    placeholder: t("auth.email"),
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    isDisabled: isResetConfirmInProgress,
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: t("auth.getAuthorisationCode")
  })), stage === Number(1) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      authorizationCode: showCode ? "" : 123456,
      password: ""
    },
    onSubmit: values => confirmResetPassword(values),
    validationSchema: Validations.forgotPasswordStage1
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: [styles$8.titleLabel, {
      marginBottom: 0
    }],
    style: {
      fontSize: currentTheme?.theme?.small_font_size
    },
    color: currentTheme?.theme?.mediumGrey
  }, showCode ? t("auth.insertCodeAndPasswordCaption") : t("auth.insertPasswordCaption")), showCode && /*#__PURE__*/React__default.createElement(AppFormCodeInput, {
    placeholder: t("auth.authorisationCode"),
    autoCapitalize: "none"
    // keyboardtype="email-address"
    ,
    autoCorrect: false,
    name: "authorizationCode",
    rootStyle: {
      marginBottom: 20
    }
  }), /*#__PURE__*/React__default.createElement(AppFormField, {
    autoComplete: Platform.OS === "web" ? "none" : "off",
    name: "password",
    autoCapitalize: "none",
    textContextType: "Name",
    placeholder: t("auth.newPassword"),
    hideShowIcon: true,
    autoCorrect: false
  }), /*#__PURE__*/React__default.createElement(AppFormField, {
    autoComplete: Platform.OS === "web" ? "none" : "off",
    placeholder: t("auth.confirmPassword"),
    autoCapitalize: "none",
    hideShowIcon: true,
    textContextType: "Name",
    autoCorrect: false,
    name: "passwordConfirmation"
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    isDisabled: isResetConfirmInProgress,
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: t("auth.resetPassword")
  })));
};
const styles$8 = StyleSheet.create({
  titleLabel: {
    marginBottom: 20,
    marginTop: 10,
    alignSelf: "center",
    maxWidth: "100%",
    fontWeight: "bold",
    textAlign: "center"
  }
});
ForgotPassword.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "ForgotPassword"
};

const SignIn = _ref => {
  let {
    iconApple = AppAssets.social.apple,
    iconEmail = AppAssets.social.create,
    iconFacebook = AppAssets.social.facebook,
    iconGoogle = AppAssets.social.google,
    iconMicrosoft = AppAssets.social.microsoft,
    iconSignIn,
    isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled,
    toggleAuthType,
    AuthButton,
    setUser,
    setFederatedSignInProgress,
    isSSOwithAzureAD,
    enableMFA = false
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const styles = styleGenerator$1();
  const signUp = () => {
    toggleAuthType("showSignUp");
  };
  React__default.useEffect(() => {
    const currentURL = window.location.href;
    if (currentURL.includes("google_signup")) {
      handleSignInWithGoogle();
    }
  }, [window.location.href]);
  const handleSignInWithGoogle = async () => {
    setFederatedSignInProgress(true);
    Auth.federatedSignIn({
      provider: "Google"
    }).then(user => {
      console.log("fed_user", user);
    }).catch(err => {
      console.log("fed_user_login_error", err);
    });
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles.container
  }, /*#__PURE__*/React__default.createElement(View, {
    style: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column"
    }
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size
    },
    wrapperStyle: styles.titleLabel,
    color: currentTheme?.theme?.mediumGrey,
    fontWeight: "bold"
  }, t("auth.tagline")), /*#__PURE__*/React__default.createElement(AppButton, {
    accessibilityLabel: "sign-in-init-button",
    title: t("auth.signIn"),
    labelStyle: {
      color: "rgba(0, 0, 0, .75)"
    },
    icon: iconSignIn,
    onPress: () => toggleAuthType("showSignInInputs"),
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 218,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
    // backgroundColor="linear-gradient(#e66465, #9198e5)"
  }), /*#__PURE__*/React__default.createElement(HorizontalSeparator, {
    text: t("auth.or")
  }), isSignInWithGoogleEnabled && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: t("auth.signInWithGoogle"),
    labelStyle: {
      color: "rgba(0, 0, 0, .75)"
    },
    fontColor: currentTheme?.theme?.black
    // borderColor={currentTheme?.theme?.darkGrey}
    ,
    icon: iconGoogle,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 218
    }],
    btnContentStyle: {
      paddingLeft: 4,
      paddingRight: 10
    },
    onPress: handleSignInWithGoogle,
    backgroundColor: currentTheme?.theme?.white,
    style: [{
      height: 35,
      minWidth: 218,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      marginHorizontal: 10,
      boxShadow: "rgba(0, 0, 0, 0.1) 3px 3px 4.84px"
    }]
  }), isSSOwithAzureAD && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: t("auth.signInWithMicrosoft"),
    labelStyle: {
      color: "rgba(0, 0, 0, .75)"
    },
    fontColor: currentTheme?.theme?.black
    // borderColor={currentTheme?.theme?.darkGrey}
    ,
    icon: iconMicrosoft,
    iconStyle: {
      height: 16,
      width: 16,
      marginLeft: 13,
      marginRight: 7
    },
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 218
    }],
    btnContentStyle: {
      paddingLeft: 4,
      paddingRight: 10
    },
    onPress: async () => {
      toggleAuthType("showSSO_SignInInputs");
    },
    backgroundColor: currentTheme?.theme?.white,
    style: [{
      height: 35,
      minWidth: 218,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      marginHorizontal: 10,
      boxShadow: "rgba(0, 0, 0, 0.1) 3px 3px 4.84px"
    }]
  }), isSignInWithAppleEnabled && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: t("auth.signInWithApple"),
    onPress: () => Auth.federatedSignIn({
      provider: "SignInWithApple"
    }),
    backgroundColor: currentTheme?.theme?.true_black,
    fontColor: currentTheme?.theme?.white,
    icon: iconApple,
    style: styles.buttonContainer
  }), isSignInWithFacebookEnabled && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: t("auth.signInWithFacebook"),
    onPress: () => Auth.federatedSignIn({
      provider: "Facebook"
    }),
    backgroundColor: "#39579A",
    fontColor: currentTheme?.theme?.white,
    icon: iconFacebook,
    style: styles.buttonContainer
  })), /*#__PURE__*/React__default.createElement(View, {
    style: {
      marginTop: 20,
      alignItems: "center",
      alignContent: "center",
      position: "relative",
      top: 20
    }
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size
    },
    wrapperStyle: styles.separatorLabel,
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.notRegisteredYet")), /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: true,
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center",
      color: currentTheme?.theme?.primary,
      fontWeight: "bold"
    },
    wrapperStyle: styles.separatorLabel,
    color: currentTheme?.theme?.primary,
    fontWeight: "bold",
    onPress: signUp
  }, t("auth.clickHereToSignUp"))));
};
const styleGenerator$1 = () => StyleSheet.create({
  buttonContainer: {
    marginTop: 10
  },
  container: {
    justifyContent: "space-between",
    flex: 1,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 10

    // alignItems: "center",
  },
  separatorLabel: {
    marginBottom: 0,
    marginTop: 10,
    alignSelf: "center"
  },
  titleLabel: {
    marginBottom: 0,
    marginTop: 10,
    alignSelf: "center",
    maxWidth: "100%",
    fontWeight: "bold",
    textAlign: "center"
  }
});
SignIn.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SignIn",
  "props": {
    "iconApple": {
      "defaultValue": {
        "value": "Assets.social.apple",
        "computed": true
      },
      "required": false
    },
    "iconEmail": {
      "defaultValue": {
        "value": "Assets.social.create",
        "computed": true
      },
      "required": false
    },
    "iconFacebook": {
      "defaultValue": {
        "value": "Assets.social.facebook",
        "computed": true
      },
      "required": false
    },
    "iconGoogle": {
      "defaultValue": {
        "value": "Assets.social.google",
        "computed": true
      },
      "required": false
    },
    "iconMicrosoft": {
      "defaultValue": {
        "value": "Assets.social.microsoft",
        "computed": true
      },
      "required": false
    },
    "enableMFA": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false
    }
  }
};

const SignInInputs = _ref => {
  let {
    onSignIn,
    toggleAuthType,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress,
    setUser
  } = _ref;
  const AppToasty = useToast();
  const {
    t
  } = useTranslation();
  const {
    currentTheme
  } = useTheme();
  const signIn = async values => {
    if (isAsyncProcessInProgress) return;
    setIsAsyncProcessInProgress(true);
    const {
      email,
      password
    } = values;
    try {
      const result = await Auth.signIn(email, password);
      console.log("Auth - successfully signed in", result);
      if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
        setUser(result);
        toggleAuthType("showForgotPasswordNoCode");
        AppToasty.show(t("auth.SignInCaptionChangePassword"), {
          placement: "top",
          type: "success"
        });
        return;
      }

      // Handle MFA challenge
      if (result.challengeName === "SOFTWARE_TOKEN_MFA") {
        setUser(result);
        toggleAuthType("showMFAVerification");
        AppToasty.show(t("auth.mfaPleaseEnterCode"), {
          placement: "top",
          type: "info"
        });
        return;
      }
      if (window?.electronAPI && window?.electronAPI?.storeTokens) {
        try {
          const electronTokens = await Auth.currentSession();
          window.electronAPI.storeTokens(electronTokens);
          console.log("Electron tokens stored", electronTokens);
          setTimeout(() => {
            console.log("_____");
          }, [5000]);
        } catch (error) {
          console.error("Renderer: Error processing login success:", error);
        }
      }
      onSignIn(result);
      AppToasty.show(t("auth.SignInCaption"), {
        placement: "top",
        type: "success"
      });
      window.location.reload(true);
    } catch (error) {
      console.log("Auth - error signing in...", error);
      if (window?.electronAPI?.writeLog) {
        window.electronAPI.writeLog("error", "Login failed", {
          email: values?.email,
          error: error?.message,
          code: error?.code
        });
      }
      AppToasty.show(t("auth.SignInFailedCaption") + " " + error?.message, {
        placement: "top",
        type: "danger"
      });
    } finally {
      setIsAsyncProcessInProgress(false);
    }
  };
  const showForgotPassword = () => {
    toggleAuthType("showForgotPassword");
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$7.container
  }, /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      username: "",
      email: "",
      password: ""
    },
    onSubmit: values => signIn(values),
    validationSchema: Validations.loginSchema
  }, /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "email",
    accessible: true,
    placeholder: t("auth.email"),
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false,
    name: "email"
  }), /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "password",
    accessible: true,
    placeholder: t("auth.password"),
    autoCapitalize: "none",
    hideShowIcon: true,
    textContextType: "password",
    autoCorrect: false,
    name: "password"
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    accessibilityLabel: "sign-in-button",
    isDisabled: isAsyncProcessInProgress,
    title: t("auth.signIn"),
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
  })), /*#__PURE__*/React__default.createElement(View, {
    style: styles$7.buttonContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size
    },
    wrapperStyle: styles$7.separatorLabel,
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.forgotPassword"))), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      alignSelf: "center",
      marginTop: 10
    },
    color: currentTheme?.theme?.mediumGrey,
    fontWeight: "bold",
    isClickable: !isAsyncProcessInProgress,
    onPress: showForgotPassword
  }, t("auth.clickHereToRecoverPassword")));
};
const styles$7 = StyleSheet.create({
  container: {
    width: "95%",
    maxWidth: 400
  },
  buttonContainer: {
    paddingTop: 30,
    justifyContent: "center",
    flexDirection: "row"
  }
});
SignInInputs.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SignInInputs"
};

const PasswordStrengthIndicator = _ref => {
  let {
    password
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const animatedWidth = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const requirements = useMemo(() => {
    const checks = [{
      id: "length",
      label: "At least 8 characters",
      test: pwd => pwd.length >= 8
    }, {
      id: "number",
      label: "Contains a number",
      test: pwd => /\d/.test(pwd)
    }, {
      id: "special",
      label: "Contains a special character",
      test: pwd => /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    }];
    return checks.map(check => ({
      ...check,
      passed: password ? check.test(password) : false
    }));
  }, [password]);
  const strength = useMemo(() => {
    const passedCount = requirements.filter(req => req.passed).length;
    const strengthLevels = [{
      level: 0,
      label: "Weak",
      color: "#FF4444",
      width: "33%"
    }, {
      level: 1,
      label: "Fair",
      color: "#FF8800",
      width: "66%"
    }, {
      level: 2,
      label: "Good",
      color: "#FFB700",
      width: "100%"
    }, {
      level: 3,
      label: "Strong",
      color: "#00C851",
      width: "100%"
    }];
    return strengthLevels[passedCount];
  }, [requirements]);
  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 300,
      useNativeDriver: true
    }).start();
    const targetWidth = password ? strength.level === 0 ? 33 : strength.level === 1 ? 66 : 100 : 0;
    Animated.timing(animatedWidth, {
      toValue: targetWidth,
      duration: 500,
      useNativeDriver: false
    }).start();
  }, [password, strength, fadeAnim, animatedWidth]);
  return /*#__PURE__*/React__default.createElement(Animated.View, {
    style: [styles$6.container, {
      opacity: fadeAnim
    }]
  }, /*#__PURE__*/React__default.createElement(View, {
    style: styles$6.strengthBar
  }, /*#__PURE__*/React__default.createElement(Animated.View, {
    style: [styles$6.strengthBarFill, {
      backgroundColor: strength.color,
      width: animatedWidth.interpolate({
        inputRange: [0, 100],
        outputRange: ["0%", "100%"]
      })
    }]
  })), /*#__PURE__*/React__default.createElement(AppText, {
    style: [styles$6.strengthLabel, {
      color: password ? strength.color : currentTheme?.theme?.mediumGrey
    }],
    fontWeight: "600"
  }, password ? strength.label : "Password Requirements"), /*#__PURE__*/React__default.createElement(View, {
    style: styles$6.requirementsList
  }, requirements.map(req => /*#__PURE__*/React__default.createElement(View, {
    key: req.id,
    style: styles$6.requirementItem
  }, /*#__PURE__*/React__default.createElement(AppIcon, {
    icon: req.passed ? AppAssets.icons.check : AppAssets.icons.uncheck,
    size: 16,
    color: req.passed ? "#00C851" : currentTheme?.theme?.mediumGrey,
    style: styles$6.requirementIcon
  }), /*#__PURE__*/React__default.createElement(AppText, {
    style: [styles$6.requirementText, {
      color: req.passed ? currentTheme?.theme?.text : currentTheme?.theme?.mediumGrey
    }]
  }, req.label)))));
};
const styles$6 = StyleSheet.create({
  container: {
    marginTop: 18,
    marginBottom: 20,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.05)"
  },
  strengthBar: {
    height: 6,
    backgroundColor: "#E8E8E8",
    borderRadius: 3,
    overflow: "hidden",
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1
  },
  strengthBarFill: {
    height: "100%",
    borderRadius: 3,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1
    },
    shadowOpacity: 0.1,
    shadowRadius: 2
  },
  strengthLabel: {
    fontSize: 14,
    marginBottom: 12,
    textAlign: "center",
    letterSpacing: 0.5
  },
  requirementsList: {
    marginTop: 4
  },
  requirementItem: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 6,
    paddingLeft: 4
  },
  requirementIcon: {
    marginRight: 10
  },
  requirementText: {
    fontSize: 14,
    letterSpacing: 0.3
  }
});
PasswordStrengthIndicator.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "PasswordStrengthIndicator"
};

// const validationSchema = Yup.object().shape({
//   // username: Yup.string().required().label("Username"),
//   email: Yup.string().required().email().label("Email"),
//   password: Yup.string().required().min(4).label("Password"),
// });

const validationSchemaConfirmation = Yup.object().shape({
  authCode: Yup.string().required().matches(/^[0-9]+$/, "Must be only digits").min(6, "Must be exactly 6 digits").max(6, "Must be exactly 6 digits").label("Confirmation code"),
  email: Yup.string().email().label("Email")
});

// Fast-signup context is persisted to sessionStorage so it survives the
// full-page redirect to "/" (which wipes the query string) and is available
// at onboarding. Onboarding consumes (clears) it once it has been read.
const FAST_SIGNUP_STORAGE_KEY = "tf_fastSignupContext";

const persistFastSignupContext = ctx => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    if (ctx && (ctx.sourceCode || ctx.referralCode)) {
      window.sessionStorage.setItem(FAST_SIGNUP_STORAGE_KEY, JSON.stringify({
        ...ctx,
        ts: Date.now()
      }));
    } else {
      window.sessionStorage.removeItem(FAST_SIGNUP_STORAGE_KEY);
    }
  } catch (e) {
    // Ignore storage errors (e.g. private mode).
  }
};
const SignUp = props => {
  const AppToasty = useToast();
  const [stage, setStage] = useState(0);
  const [isSignUpInProgress, setSignUpInProgress] = useState(false);
  const [isConfirmationInProgress, setConfirmationInProgress] = useState(false);
  const [isForcedEmail, setForcedEmail] = useState(false);
  const [username, setUsername] = useState(null);
  const [email, setEmail] = useState(null);
  const [lastResortEmail, setLastResortEmail] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [urlSourceCode, setUrlSourceCode] = useState(null);
  const [urlReferralCode, setUrlReferralCode] = useState(null);
  // In-memory only: holds the password transiently so the user doesn't have
  // to re-type it after confirming the email code. NEVER persisted to storage.
  const [cachedPassword, setCachedPassword] = useState(null);
  const {
    t
  } = useTranslation();
  const {
    currentTheme
  } = useTheme();
  function decrypt(input) {
    var key = "1.0"; // Use the same secret key that was used to encrypt
    // Decode the Base64 input string before decryption
    var decodedInput = atob(input);
    var output = [];
    for (var i = 0; i < decodedInput.length; i++) {
      var charCode = decodedInput.charCodeAt(i) ^ key[i % key.length].charCodeAt(0);
      output.push(String.fromCharCode(charCode));
    }
    return output.join("");
  }
  // check if the url ends with code_confirmation and if so, set the stage to 1
  React__default.useEffect(() => {
    const currentURL = window.location.href;
    if (currentURL.includes("code_confirmation")) {
      setStage(1);
      setEmail(decrypt(currentURL?.split("code_confirmation/")?.pop()));
    }

    // Check for referral code in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get("ref") || urlParams.get("referral");
    if (refCode) {
      setReferralCode(refCode);
    }

    // Fast-signup: read sourceCode/referralCode directly from the URL
    const sourceCode = urlParams.get("sourceCode");
    const voucherCode = urlParams.get("referralCode");
    if (sourceCode) {
      setUrlSourceCode(sourceCode.toUpperCase());
    }
    if (voucherCode) {
      setUrlReferralCode(voucherCode);
    }
    // Persist the fast-signup context (or clear it when absent) so it
    // survives the redirect to "/" and is available at onboarding.
    persistFastSignupContext({
      sourceCode: sourceCode ? sourceCode.toUpperCase() : null,
      referralCode: voucherCode || null
    });
  }, [window.location.href]);
  // Clear the in-memory password if the component unmounts.
  React__default.useEffect(() => {
    return () => {
      setCachedPassword(null);
    };
  }, []);
  const signUp = async values => {
    if (isSignUpInProgress) return AppToasty.show(t("auth.SignUpInProgressCaption"), {
      placement: "top",
      type: "warning"
    });

    // Validate referral code if provided
    const {
      password,
      email,
      referralCode
    } = values;
    setSignUpInProgress(true);
    try {
      const signUpAttributes = {
        email
      };
      if (referralCode && referralCode.trim() !== "") {
        signUpAttributes["custom:referralCode"] = referralCode.trim().toUpperCase();
      }
      // Fast-signup (/fastSignup): carry the URL referral code through. Its
      // presence is the backend auto-confirm signal (no separate channel attr).
      const fastReferralCode = urlReferralCode || referralCode;
      const isFastSignup = urlSourceCode && fastReferralCode && fastReferralCode.trim() !== "";
      if (isFastSignup) {
        signUpAttributes["custom:referralCode"] = fastReferralCode.trim().toUpperCase();
      }
      await Auth.signUp({
        username: email,
        password,
        attributes: signUpAttributes
      });
      AppToasty.show(t("auth.SignUpCaption"), {
        placement: "top",
        type: "success"
      });
      console.log("successful sign up..");
      // Fast-signup: the backend has auto-confirmed the user, so sign in
      // straight away and skip the email confirmation stage.
      if (isFastSignup) {
        await Auth.signIn(email, password);
        window.location.href = "/";
        return;
      }
      // Normal signup: hold the password in-memory so after confirming the
      // email code the user doesn't have to type it again.
      setCachedPassword(password);
      setStage(1);
      setUsername(email);
      setEmail(email);
    } catch (err) {
      AppToasty.show(t("auth.SignUpFailedCaption") + err?.message, {
        placement: "top",
        type: "danger"
      });
      console.error("error signing up...", err);
      setSignUpInProgress(false);
    }
  };
  const confirmSignUp = async values => {
    if (isConfirmationInProgress) return AppToasty.show(t("auth.SignUpInProgressCaption"), {
      placement: "top",
      type: "warning"
    });
    setConfirmationInProgress(true);
    const {
      authCode
    } = values;
    const username = email || values.email;
    const code = authCode;
    try {
      await Auth.confirmSignUp(username, code);
      AppToasty.show(t("auth.signUpSuccess"), {
        placement: "top",
        type: "success"
      });
      // Sign the user in straight away using the in-memory password, so they
      // don't have to re-type it after confirming the email code.
      if (cachedPassword) {
        const _password = cachedPassword;
        setCachedPassword(null); // clear asap
        try {
          await Auth.signIn(username, _password);
        } catch (signInErr) {
          console.error("auto sign-in error after confirm", signInErr);
        }
      }
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    } catch (err) {
      AppToasty.show(t("auth.SignUpFailedCaption") + err?.message, {
        placement: "top",
        type: "danger"
      });
      console.error("error signing up...", err);
      setConfirmationInProgress(false);
    }
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$5.container
  }, stage === Number(0) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      username: "",
      email: "",
      password: "",
      referralCode: referralCode || ""
    },
    onSubmit: values => signUp(values),
    validationSchema: Validations.loginSchema
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: styles$5.titleLabel,
    style: {
      fontSize: currentTheme?.theme?.small_font_size
    },
    color: currentTheme?.theme?.mediumGrey,
    fontWeight: "bold"
  }, t("auth.welcomeMessage")), /*#__PURE__*/React__default.createElement(AppFormField
  // leftIcon="email"
  // backgroundColor=""
  , {
    placeholder: t("auth.email"),
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false,
    name: "email"
  }), /*#__PURE__*/React__default.createElement(AppFormField
  // leftIcon="lock"
  // backgroundColor="rgba(245, 245, 245, 1)"
  , {
    placeholder: t("auth.password"),
    autoCapitalize: "none",
    hideShowIcon: true,
    textContextType: "password",
    autoCorrect: false,
    name: "password",
    onChange: value => setPasswordValue(value)
  }), /*#__PURE__*/React__default.createElement(PasswordStrengthIndicator, {
    password: passwordValue
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    isDisabled: isSignUpInProgress,
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: t("auth.signUpButton")
  }), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      alignSelf: "center",
      marginTop: 15
    },
    color: currentTheme?.theme?.mediumGrey,
    fontWeight: "bold",
    isClickable: true,
    onPress: () => {
      setStage(1);
      setForcedEmail(true);
    }
  }, t("auth.clickHereToEnterConfirmationCode"))), stage === Number(1) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      authCode: ""
    },
    onSubmit: values => confirmSignUp(values),
    validationSchema: validationSchemaConfirmation
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: styles$5.titleLabel,
    style: {
      fontSize: currentTheme?.theme?.small_font_size
    },
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.insertCodeCaption")), isForcedEmail && /*#__PURE__*/React__default.createElement(AppFormField, {
    placeholder: t("auth.email"),
    autoCapitalize: "none"
    // keyboardtype="email-address"
    ,
    textContextType: "Name",
    autoCorrect: false,
    name: "email",
    onChange: v => {
      setLastResortEmail(v);
    }
  }), /*#__PURE__*/React__default.createElement(AppFormCodeInput, {
    placeholder: t("auth.authorisationCode"),
    autoCapitalize: "none"
    // keyboardtype="email-address"
    ,
    autoCorrect: false,
    name: "authCode",
    rootStyle: {
      marginBottom: 40
    }
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: t("auth.confirmSignUpButton"),
    isDisabled: isConfirmationInProgress
  }), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      marginTop: 20,
      justifyContent: "center"
    },
    color: currentTheme?.theme?.mediumGrey,
    preventTranslation: true,
    fontWeight: "bold"
  }, /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: true,
    preventTranslation: true,
    onPress: async () => {
      try {
        await Auth.resendSignUp(username || lastResortEmail);
        AppToasty.show(t("auth.SignUpEmailResentCaption"), {
          placement: "top",
          type: "success"
        });
      } catch (error) {
        AppToasty.show(t("auth.SignUpEmailResentErrorCaption"), {
          placement: "top",
          type: "danger"
        });
      }
    },
    style: {
      fontWeight: "normal",
      color: currentTheme?.theme?.primary,
      fontSize: currentTheme?.theme?.small_font_size
    },
    fontWeight: "bold"
  }, t("auth.resendConfirmationCode")), " "), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      marginTop: 20,
      justifyContent: "center"
    },
    color: currentTheme?.theme?.mediumGrey,
    fontWeight: "bold",
    isClickable: true,
    onPress: () => {
      setStage(0);
      setForcedEmail(false);
    }
  }, t("auth.clickHereToGoBackToSignup"))));
};
const styles$5 = StyleSheet.create({
  container: {
    width: "95%",
    maxWidth: 400
  },
  input: {
    backgroundColor: "#fcf3db",
    borderRadius: 30,
    height: 45
  },
  titleLabel: {
    marginBottom: 20,
    marginTop: 10,
    alignSelf: "center",
    maxWidth: "100%",
    fontWeight: "bold",
    textAlign: "center"
  }
});
SignUp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SignUp"
};

const SSO_SignInInputs = _ref => {
  let {
    onSignIn,
    toggleAuthType,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress,
    setUser,
    setFederatedSignInProgress,
    onProviderNotFound,
    getSSOproviderName
  } = _ref;
  const AppToasty = useToast();
  const {
    t
  } = useTranslation();
  const {
    currentTheme
  } = useTheme();
  const signIn = async values => {
    if (isAsyncProcessInProgress) return;
    try {
      setIsAsyncProcessInProgress(true);
      setFederatedSignInProgress(true);
      // get the domain from an email
      const _domain = values.email.split("@")[1];
      const result = await getSSOproviderName(_domain);
      if (!result?.providerName) {
        setFederatedSignInProgress(false);
        onProviderNotFound();
        return;
      }
      // mapping between domain of the email and the existing providers on cognito
      // ssoProviderName
      Auth.federatedSignIn({
        provider: result?.providerName
      }).then(user => {
        console.log("fed_user", user);
      }).catch(err => {
        console.log("fed_user_login_error", err);
      });
    } catch (error) {
      console.log("Auth - error signing in...", error);
      AppToasty.show(t("SignInFailedCaption") + " " + error?.message, {
        placement: "top",
        type: "danger"
      });
    } finally {
      setIsAsyncProcessInProgress(false);
    }
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$4.container
  }, /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      username: "",
      email: "",
      password: ""
    },
    onSubmit: values => signIn(values),
    validationSchema: Validations.ssoSchema
  }, /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "email",
    accessible: true,
    placeholder: t("auth.enterWorkEmail"),
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false,
    name: "email"
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    accessibilityLabel: "sign-in-button",
    isDisabled: isAsyncProcessInProgress,
    title: t("auth.signInWithSSO"),
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
  })), /*#__PURE__*/React__default.createElement(View, {
    style: styles$4.buttonContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size
    },
    wrapperStyle: styles$4.separatorLabel,
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.ssoDescription")), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      alignSelf: "center",
      marginTop: 10
    },
    color: currentTheme?.theme?.mediumGrey,
    fontWeight: "bold",
    isClickable: !isAsyncProcessInProgress,
    onPress: () => toggleAuthType("showSignIn")
  }, t("auth.clickHereToGoBackToSignIn"))));
};
const styles$4 = StyleSheet.create({
  container: {
    maxWidth: 400
  },
  buttonContainer: {
    paddingTop: 30,
    justifyContent: "center",
    flexDirection: "column"
  }
});
SSO_SignInInputs.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SSO_SignInInputs"
};

const isElectron$2 = !!window?.electronAPI;
const MFAVerification = _ref => {
  let {
    onSignIn,
    toggleAuthType,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress,
    user
  } = _ref;
  const {
    t
  } = useTranslation();
  const AppToasty = useToast();
  const {
    currentTheme
  } = useTheme();
  const verifyMFA = async values => {
    if (isAsyncProcessInProgress) return;
    setIsAsyncProcessInProgress(true);
    const {
      mfaCode
    } = values;
    try {
      let result;
      if (isElectron$2) {
        // For Electron, use the sign-in verification (not setup verification)
        // This is for users who already have MFA configured
        const mfaResult = await window.electronAPI.verifyMfaSignin(mfaCode);
        if (!mfaResult.success) {
          throw new Error(mfaResult.error || "Invalid authenticator code");
        }
        result = {
          authenticated: true
        };
        console.log("Electron TOTP MFA verification successful");
      } else {
        // Web flow - use Amplify Auth
        result = await Auth.confirmSignIn(user, mfaCode, "SOFTWARE_TOKEN_MFA");
        console.log("TOTP MFA verification successful", result);
        if (window?.electronAPI && window?.electronAPI?.storeTokens) {
          try {
            const electronTokens = await Auth.currentSession();
            window.electronAPI.storeTokens(electronTokens);
            console.log("Electron tokens stored", electronTokens);
          } catch (error) {
            console.error("Renderer: Error processing login success:", error);
          }
        }
      }
      onSignIn(result);
      AppToasty.show(t("auth.mfa.success"), {
        placement: "top",
        type: "success"
      });
      window.location.reload(true);
    } catch (error) {
      console.log("TOTP MFA verification error", error);
      AppToasty.show(t("auth.mfa.invalidCodeWithError", {
        errorMessage: error?.message
      }), {
        placement: "top",
        type: "danger"
      });
    } finally {
      setIsAsyncProcessInProgress(false);
    }
  };
  const goBackToSignIn = () => {
    toggleAuthType("showSignInInputs");
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$3.container
  }, /*#__PURE__*/React__default.createElement(View, {
    style: styles$3.header
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.medium_font_size,
      textAlign: "center",
      marginBottom: 10
    },
    wrapperStyle: {
      justifyContent: "center"
    },
    color: currentTheme?.theme?.primary,
    fontWeight: "bold"
  }, t("auth.mfa.title")), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center",
      marginBottom: 20
    },
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.mfa.instructions"))), /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      mfaCode: ""
    },
    onSubmit: values => verifyMFA(values),
    validationSchema: Validations.mfaVerificationSchema
  }, /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "authenticator-code",
    accessible: true,
    placeholder: t("auth.mfa.placeholder"),
    autoCapitalize: "none",
    keyboardType: "numeric",
    autoCorrect: false,
    name: "mfaCode",
    maxLength: 6,
    textAlign: "center",
    style: {
      fontSize: 20,
      letterSpacing: 8,
      fontWeight: "bold",
      textAlign: "center"
    }
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    accessibilityLabel: "verify-mfa-button",
    isDisabled: isAsyncProcessInProgress,
    title: isAsyncProcessInProgress ? t("auth.mfa.verifying") : t("auth.mfa.verify"),
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }],
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
  })), /*#__PURE__*/React__default.createElement(View, {
    style: styles$3.buttonContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center",
      marginBottom: 20
    },
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.mfa.codeChangesEvery")), /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: !isAsyncProcessInProgress,
    onPress: goBackToSignIn,
    color: currentTheme?.theme?.mediumGrey,
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    }
  }, t("auth.mfa.backToSignIn"))));
};
const styles$3 = StyleSheet.create({
  container: {
    width: "95%",
    maxWidth: 400
  },
  header: {
    marginBottom: 20
  },
  buttonContainer: {
    paddingTop: 30,
    justifyContent: "center",
    alignItems: "center"
  }
});
MFAVerification.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "MFAVerification"
};

const isElectron$1 = !!window?.electronAPI;
const MFASetup = _ref => {
  let {
    onMFASetupComplete,
    onCancel,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress
  } = _ref;
  const {
    t
  } = useTranslation();
  const AppToasty = useToast();
  const {
    currentTheme
  } = useTheme();
  const [step, setStep] = useState("setup"); // "setup" | "verify"
  const [qrCodeUri, setQrCodeUri] = useState("");
  const [qrCodeImage, setQrCodeImage] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const setupTOTP = async () => {
    if (isAsyncProcessInProgress) return;
    setIsAsyncProcessInProgress(true);
    try {
      let totpCode, userEmail;
      const appName = "ToothFairyAI";
      if (isElectron$1) {
        // Electron flow - use IPC to generate TOTP secret
        const result = await window.electronAPI.setupMfaTotp();
        if (!result.success) {
          throw new Error(result.error || "Failed to setup TOTP");
        }
        totpCode = result.secret;
        userEmail = result.email;
        console.log("Electron TOTP setup response:", {
          secret: totpCode,
          email: userEmail
        });
      } else {
        // Web flow - use Amplify Auth
        const user = await Auth.currentAuthenticatedUser();
        totpCode = await Auth.setupTOTP(user);
        userEmail = user.attributes?.email || user.username;
        console.log("TOTP setup response:", totpCode);
      }

      // Generate QR code URI for authenticator apps
      const qrUri = `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(userEmail)}?secret=${totpCode}&issuer=${encodeURIComponent(appName)}`;
      setQrCodeUri(qrUri);
      setSecretKey(totpCode);

      // Generate QR code image
      try {
        const qrImageDataUrl = await QRCode.toDataURL(qrUri);
        setQrCodeImage(qrImageDataUrl);
      } catch (qrError) {
        console.log("QR code generation error", qrError);
      }
      setStep("verify");
      setIsAsyncProcessInProgress(false);

      // AppToasty.show("Please scan the QR code with your authenticator app", {
      //   placement: "top",
      //   type: "success",
      // });
    } catch (error) {
      console.log("TOTP setup error", error);
      AppToasty.show(t("auth.mfa.setupFailed") + " " + error?.message, {
        placement: "top",
        type: "danger"
      });
      setIsAsyncProcessInProgress(false);
    }
  };
  const verifyTOTP = async values => {
    if (isAsyncProcessInProgress) return;
    setIsAsyncProcessInProgress(true);
    const {
      totpCode
    } = values;
    try {
      if (isElectron$1) {
        // Electron flow - verify through IPC
        const result = await window.electronAPI.verifyMfaTotp(totpCode);
        if (!result.success) {
          throw new Error(result.error || t("auth.mfa.invalidCode"));
        }
        AppToasty.show(result.message || t("auth.mfa.enabledSuccess"), {
          placement: "top",
          type: "success"
        });
      } else {
        // Web flow - use Amplify Auth
        const user = await Auth.currentAuthenticatedUser();

        // Verify TOTP code
        await Auth.verifyTotpToken(user, totpCode);

        // Set TOTP as preferred MFA method
        await Auth.setPreferredMFA(user, "TOTP");
        AppToasty.show(t("auth.mfa.enabledSuccess"), {
          placement: "top",
          type: "success"
        });
      }
      onMFASetupComplete(true);
    } catch (error) {
      console.log("TOTP verification error", error);
      AppToasty.show(t("auth.mfa.invalidCodeRetry") + " " + error?.message, {
        placement: "top",
        type: "danger"
      });
    } finally {
      setIsAsyncProcessInProgress(false);
    }
  };
  useEffect(() => {
    if (step === "setup") {
      setupTOTP();
    }
  }, [step]);
  if (step === "setup") {
    return /*#__PURE__*/React__default.createElement(View, {
      style: styles$2.container
    }, /*#__PURE__*/React__default.createElement(View, {
      style: styles$2.header
    }, /*#__PURE__*/React__default.createElement(AppText, {
      style: {
        fontSize: currentTheme?.theme?.medium_font_size,
        marginBottom: 10
      },
      color: currentTheme?.theme?.textPrimary,
      fontWeight: "bold"
    }, t("auth.mfa.settingUp")), /*#__PURE__*/React__default.createElement(AppText, {
      style: {
        fontSize: currentTheme?.theme?.small_font_size,
        marginBottom: 20
      },
      color: currentTheme?.theme?.mediumGrey
    }, t("auth.mfa.pleaseWait"))), /*#__PURE__*/React__default.createElement(View, {
      style: styles$2.buttonContainer
    }, /*#__PURE__*/React__default.createElement(AppText, {
      isClickable: !isAsyncProcessInProgress,
      onPress: onCancel,
      color: currentTheme?.theme?.mediumGrey,
      style: {
        fontSize: currentTheme?.theme?.small_font_size,
        textAlign: "center"
      }
    }, t("common.cancel"))));
  }
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$2.container
  }, /*#__PURE__*/React__default.createElement(View, {
    style: styles$2.header
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.medium_font_size,
      textAlign: "center",
      marginBottom: 10
    },
    color: currentTheme?.theme?.textPrimary,
    fontWeight: "bold"
  }, t("auth.mfa.setupApp")), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      marginBottom: 20
    },
    color: currentTheme?.theme?.primary
  }, t("auth.mfa.instructions")), /*#__PURE__*/React__default.createElement(View, {
    style: styles$2.qrCodeContainer
  }, qrCodeImage ? /*#__PURE__*/React__default.createElement(Image, {
    source: {
      uri: qrCodeImage
    },
    style: {
      width: 200,
      height: 200,
      marginBottom: 15
    },
    resizeMode: "contain"
  }) : /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center",
      marginBottom: 10
    },
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.mfa.generatingQR")), secretKey && /*#__PURE__*/React__default.createElement(View, {
    style: styles$2.manualEntry
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center",
      marginBottom: 5
    },
    color: currentTheme?.theme?.mediumGrey
  }, t("auth.mfa.cantScan")), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center",
      fontFamily: "monospace",
      backgroundColor: currentTheme?.theme?.lightGrey || "#f5f5f5",
      padding: 10,
      borderRadius: 5,
      wordBreak: "break-all",
      flexWrap: "wrap"
    },
    color: currentTheme?.theme?.textPrimary,
    preventTranslation: true,
    showCopyButton: true
  }, secretKey)))), /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      totpCode: ""
    },
    onSubmit: values => verifyTOTP(values),
    validationSchema: Validations.totpSetupSchema
  }, /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "totp-code",
    accessible: true,
    placeholder: t("auth.mfa.placeholder"),
    autoCapitalize: "none",
    keyboardType: "numeric",
    autoCorrect: false,
    name: "totpCode",
    maxLength: 6,
    textAlign: "center",
    style: {
      fontSize: 20,
      letterSpacing: 8,
      fontWeight: "bold",
      textAlign: "center"
    }
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    accessibilityLabel: "verify-totp-button",
    isDisabled: isAsyncProcessInProgress,
    title: isAsyncProcessInProgress ? t("auth.mfa.verifying") : t("auth.mfa.completeSetup"),
    backgroundColor: currentTheme?.theme?.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme?.theme?.transparent,
      marginHorizontal: 10
    }],
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
  })), /*#__PURE__*/React__default.createElement(View, {
    style: styles$2.buttonContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: !isAsyncProcessInProgress,
    onPress: onCancel,
    color: currentTheme?.theme?.mediumGrey,
    style: {
      fontSize: currentTheme?.theme?.small_font_size,
      textAlign: "center"
    }
  }, t("auth.mfa.cancelSetup"))));
};
const styles$2 = StyleSheet.create({
  container: {
    width: "95%",
    maxWidth: 400
  },
  header: {
    marginBottom: 20
  },
  qrCodeContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 20,
    padding: 20,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 10,
    backgroundColor: "#fafafa"
  },
  manualEntry: {
    marginTop: 15,
    width: "100%"
  },
  buttonContainer: {
    paddingTop: 30,
    justifyContent: "center",
    alignItems: "center"
  }
});
MFASetup.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "MFASetup"
};

const BaseContentSection = _ref => {
  let {
    iconApple,
    iconEmail,
    iconFacebook,
    iconMicrosoft,
    iconGoogle,
    iconSignIn,
    showSignUp,
    showForgotPassword,
    showSignInInputs,
    showSignIn,
    showSSO_SignInInputs,
    showMFAVerification,
    showMFASetup,
    toggleAuthType,
    onSignIn,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress,
    isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled,
    AuthButton,
    showForgotPasswordNoCode,
    user,
    setUser,
    setFederatedSignInProgress,
    isSSOwithAzureAD,
    getSSOproviderName,
    onProviderNotFound,
    enableMFA = false
  } = _ref;
  const dimensions = useWindowDimensions();
  const [isLargeScreen, setLargeScreen] = useState(dimensions?.width >= 768);
  useEffect(() => {
    const _largeScreen = dimensions?.width >= 768;
    setLargeScreen(_largeScreen);
  }, [dimensions?.width]);
  return /*#__PURE__*/React__default.createElement(View, {
    style: {
      // maxWidth: isLargeScreen ? 450 : 320,
      minWidth: isLargeScreen ? 350 : 320,
      flex: 1
    }
  }, showSignIn && /*#__PURE__*/React__default.createElement(SignIn, {
    isSignInWithGoogleEnabled: isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled: isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled: isSignInWithFacebookEnabled,
    iconApple: iconApple,
    iconEmail: iconEmail,
    iconFacebook: iconFacebook,
    iconGoogle: iconGoogle,
    iconSignIn: iconSignIn,
    iconMicrosoft: iconMicrosoft,
    toggleAuthType: toggleAuthType,
    updateAuth: () => toggleAuthType("mainNav"),
    onProviderNotFound: onProviderNotFound,
    AuthButton: AuthButton,
    setUser: setUser,
    setFederatedSignInProgress: setFederatedSignInProgress,
    isSSOwithAzureAD: isSSOwithAzureAD,
    getSSOproviderName: getSSOproviderName,
    enableMFA: enableMFA
  }), showSignInInputs && /*#__PURE__*/React__default.createElement(SignInInputs, {
    onSignIn: onSignIn,
    toggleAuthType: toggleAuthType,
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress,
    AuthButton: AuthButton,
    setUser: setUser
  }), showSSO_SignInInputs && /*#__PURE__*/React__default.createElement(SSO_SignInInputs, {
    onSignIn: onSignIn,
    toggleAuthType: toggleAuthType,
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress,
    AuthButton: AuthButton,
    setUser: setUser,
    setFederatedSignInProgress: setFederatedSignInProgress,
    getSSOproviderName: getSSOproviderName,
    onProviderNotFound: onProviderNotFound
  }), showSignUp && /*#__PURE__*/React__default.createElement(SignUp, {
    toggleAuthType: toggleAuthType,
    AuthButton: AuthButton
  }), (showForgotPassword || showForgotPasswordNoCode) && /*#__PURE__*/React__default.createElement(ForgotPassword, {
    toggleAuthType: toggleAuthType,
    AuthButton: AuthButton,
    showCode: !showForgotPasswordNoCode,
    user: user
  }), enableMFA && showMFAVerification && /*#__PURE__*/React__default.createElement(MFAVerification, {
    onSignIn: onSignIn,
    toggleAuthType: toggleAuthType,
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress,
    user: user
  }), enableMFA && showMFASetup && /*#__PURE__*/React__default.createElement(MFASetup, {
    onMFASetupComplete: success => {
      if (success) {
        toggleAuthType("showSignIn");
      }
    },
    onCancel: () => toggleAuthType("showSignIn"),
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress
  }));
};
BaseContentSection.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "BaseContentSection",
  "props": {
    "enableMFA": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false
    }
  }
};

const BaseFooterSection = _ref => {
  let {
    showSignUp,
    showForgotPassword,
    showSignInInputs,
    toggleAuthType,
    isAsyncProcessInProgress
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  return /*#__PURE__*/React__default.createElement(View, {
    style: {
      position: "relative",
      paddingTop: 30
    }
  }, (showSignInInputs || showSignUp || showForgotPassword) && /*#__PURE__*/React__default.createElement(AppText, {
    leftIcon: AppAssets.icons.back_icon,
    color: currentTheme?.theme?.primary,
    style: {
      ...styles$1.bottomMessageHighlight,
      fontSize: currentTheme?.theme?.small_font_size
    },
    isClickable: !isAsyncProcessInProgress,
    onPress: () => toggleAuthType("showSignIn"),
    wrapperStyle: {
      paddingBottom: 10
    },
    iconSize: 16,
    fontWeight: "bold"
  }, "Back to sign in options"));
};
const styles$1 = StyleSheet.create({
  bottomMessageHighlight: {
    // color: "#00cfd0",
    // textDecorationStyle: "solid",
    // paddingLeft: 10,
    // paddingBottom: 40,
  }
});
BaseFooterSection.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "BaseFooterSection"
};

Dimensions.get("window");
const BaseHeaderSection = _ref => {
  let {
    showSignUp,
    showForgotPassword,
    showSignInInputs,
    toggleAuthType,
    brandImage
  } = _ref;
  useState(null);
  return /*#__PURE__*/React__default.createElement(Image, {
    style: styles.logo,
    resizeMode: "contain",
    source: brandImage
  });
};
const styles = StyleSheet.create({
  logo: {
    height: 50,
    width: 200,
    margin: "auto",
    marginBottom: 10,
    resizeMode: "contain"
  }
});
BaseHeaderSection.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "BaseHeaderSection"
};

const AuthScreen = _ref => {
  let {
    brandImage,
    bgVideo,
    config,
    iconApple,
    iconEmail,
    iconFacebook,
    iconGoogle,
    iconSignIn,
    iconMicrosoft,
    onSignIn,
    isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled,
    AuthButton = AppButton,
    setFederatedSignInProgress,
    isSSOwithAzureAD,
    getSSOproviderName = () => {},
    onProviderNotFound = () => {},
    enableMFA = true
  } = _ref;
  const dimensions = useWindowDimensions();
  const [isLargeScreen, setLargeScreen] = useState(dimensions?.width >= 768);
  useEffect(() => {
    const _largeScreen = dimensions?.width >= 768;
    setLargeScreen(_largeScreen);
  }, [dimensions?.width]);
  const [formType, setFormType] = useState("showSignIn");
  useState(false);
  const [user, setUser] = useState(null);
  const [isAsyncProcessInProgress, setIsAsyncProcessInProgress] = useState(false);
  Auth.configure(config);
  const toggleAuthType = formType => {
    setFormType(formType);
  };
  // get the exact path of the current screen
  const currentURL = window.location.href;
  useEffect(() => {
    if (currentURL.split("/").includes("signUp") || currentURL.split("/").includes("code_confirmation") || currentURL.split("/").includes("fastSignup")) {
      setFormType("showSignUp");
    }
  }, [currentURL]);
  const showSignIn = formType === "showSignIn";
  const showSignInInputs = formType === "showSignInInputs";
  const showSignUp = formType === "showSignUp";
  const showForgotPassword = formType === "showForgotPassword";
  const showForgotPasswordNoCode = formType === "showForgotPasswordNoCode";
  const showSSO_SignInInputs = formType === "showSSO_SignInInputs";
  const showMFAVerification = formType === "showMFAVerification";
  const showMFASetup = formType === "showMFASetup";
  useTheme();
  const styles = styleGenerator();
  return /*#__PURE__*/React__default.createElement(KeyboardAvoidingView, {
    style: styles.container,
    behavior: Platform.OS == "ios" ? "padding" : "height"
  }, /*#__PURE__*/React__default.createElement(View, {
    style: {
      ...styles.form,
      width: isLargeScreen ? "50%" : "100%"
    }
  }, /*#__PURE__*/React__default.createElement(BaseHeaderSection, {
    brandImage: brandImage
  }), /*#__PURE__*/React__default.createElement(BaseContentSection, {
    iconApple: iconApple,
    iconEmail: iconEmail,
    iconFacebook: iconFacebook,
    iconGoogle: iconGoogle,
    iconMicrosoft: iconMicrosoft,
    iconSignIn: iconSignIn,
    isSignInWithGoogleEnabled: isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled: isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled: isSignInWithFacebookEnabled,
    toggleAuthType: toggleAuthType,
    onSignIn: onSignIn,
    showSignIn: showSignIn,
    showForgotPassword: showForgotPassword,
    showSignInInputs: showSignInInputs,
    showSignUp: showSignUp,
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    showSSO_SignInInputs: showSSO_SignInInputs,
    showMFAVerification: showMFAVerification,
    showMFASetup: showMFASetup,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress,
    AuthButton: AuthButton,
    showForgotPasswordNoCode: showForgotPasswordNoCode,
    setUser: setUser,
    getSSOproviderName: getSSOproviderName,
    onProviderNotFound: onProviderNotFound,
    user: user,
    setFederatedSignInProgress: setFederatedSignInProgress,
    isSSOwithAzureAD: isSSOwithAzureAD,
    enableMFA: enableMFA
  }), /*#__PURE__*/React__default.createElement(BaseFooterSection, {
    toggleAuthType: toggleAuthType,
    showSignIn: showSignIn,
    showForgotPassword: showForgotPassword,
    showSignInInputs: showSignInInputs,
    showSignUp: showSignUp,
    isAsyncProcessInProgress: isAsyncProcessInProgress
  })), isLargeScreen && /*#__PURE__*/React__default.createElement("div", {
    style: {
      width: isLargeScreen ? "50%" : "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
      // backgroundColor: "#000"
    }
  }, /*#__PURE__*/React__default.createElement("video", {
    style: {
      width: "100%",
      maxWidth: "500px",
      height: "auto",
      aspectRatio: "676/584",
      objectFit: "cover",
      overflow: "hidden",
      borderRadius: "10px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      marginRight: "20px",
      marginLeft: "10px"
    },
    src: bgVideo,
    autoPlay: true,
    muted: true,
    playsInline: true,
    onEnded: e => {
      setTimeout(() => {
        e.target.currentTime = 0;
        e.target.play();
      }, 1000);
    }
  })));
};
const styleGenerator = () => StyleSheet.create({
  container: {
    alignItems: "center",
    // backgroundColor: "#0B1019",
    flex: 1,
    flexDirection: "row",
    height: "100%"
  },
  bg: {
    height: "100%"
  },
  form: {
    alignItems: "center",
    flexDirection: "column"
  }
});
AuthScreen.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AuthScreen",
  "props": {
    "AuthButton": {
      "defaultValue": {
        "value": "AppButton",
        "computed": true
      },
      "required": false
    },
    "getSSOproviderName": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false
    },
    "onProviderNotFound": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false
    },
    "enableMFA": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "required": false
    }
  }
};

const isElectron = !!window?.electronAPI;
const getCurrentAuthenticatedUser = async () => {
  if (isElectron) {
    // --- Electron Flow ---

    if (!window.electronAPI?.getUserClaims) {
      return null;
    }
    try {
      // This IPC handler should retrieve stored tokens and return the decoded ID token claims
      // It should also handle expiry checks internally.
      const claims = await window.electronAPI.getUserClaims();
      if (!claims) {}
      return claims; // Return the claims object or null
    } catch (error) {
      console.error("AuthUtil: Error invoking electronAPI.getUserClaims:", error);
      return null;
    }
  } else {
    // --- Web Flow (using Amplify) ---

    try {
      // Use the core Amplify Auth directly
      // Add bypassCache option for potentially fresher data, though usually not needed here
      const user = await Auth$1.currentAuthenticatedUser({
        bypassCache: false
      });

      // Extract the ID token payload which contains the claims/attributes
      // This provides data consistent with what the Electron flow returns
      const claims = user?.signInUserSession?.idToken?.payload;
      if (!claims) {
        console.warn("AuthUtil: Amplify user authenticated but failed to get ID token payload.");
        // Fallback? Maybe return basic user attributes if available?
        // For consistency, return null if payload isn't there.
        return null;
      }
      console.log("AuthUtil: Extracted claims from Amplify user session.");
      return claims;
    } catch (error) {
      // Amplify typically throws an error string 'No current user' if not logged in
      console.log("AuthUtil: Amplify Auth.currentAuthenticatedUser failed (likely not logged in):", error);
      return null; // Return null if no session / not authenticated
    }
  }
};
const getCurrentSession = () => {
  if (isElectron) {
    // --- Electron Flow ---

    if (!window.electronAPI?.getAccessToken) {
      return null;
    }
    try {
      return window.electronAPI.getIdToken().then(result_ => {
        return {
          idToken: {
            jwtToken: result_
          }
        };
      });

      // This Electron API call should handle refresh internally (as implemented in main.js)
    } catch (error) {
      console.log("AuthUtil: Error invoking electronAPI.getAccessToken:", error);
      return null;
    }
  } else {
    // --- Web Flow (using Amplify) ---
    try {
      // Use the core Amplify Auth directly
      return Auth$1.currentSession();
      // session object structure might vary slightly, ensure paths are correct
      // const token = session?.getAccessToken()?.getJwtToken();
      // if (!token) {
      //     console.warn('AuthUtil: Amplify session valid but no access token JWT found.');
      // }
      // return token;
    } catch (error) {
      // Amplify typically throws an error string 'No current user' if not logged in
      console.log("AuthUtil: Amplify Auth.currentSession failed (likely not logged in):", error);
      return null; // Return null if no session / not authenticated
    }
  }
};

/**
 * Gets the current valid ID token JWT string, handling both Electron and Web flows.
 * Returns the JWT string or null if not authenticated or an error occurs.
 * NOTE: This does NOT trigger refresh in Electron; expiry is checked.
 */
const getCurrentIdToken = async () => {
  if (isElectron) {
    // --- Electron Flow ---
    if (!window.electronAPI?.getIdToken) {
      console.log("AuthUtil: window.electronAPI.getIdToken is not available!");
      return null;
    }
    try {
      const token = await window.electronAPI.getIdToken(); // Calls the new handler
      if (!token) {
        console.log("AuthUtil: Electron API returned null ID token (not logged in or expired).");
      }
      return token;
    } catch (error) {
      console.error("AuthUtil: Error invoking electronAPI.getIdToken:", error);
      return null;
    }
  } else {
    // --- Web Flow (using Amplify) ---
    try {
      const session = await Auth$1.currentSession();
      const token = session?.getIdToken()?.getJwtToken(); // Get ID Token JWT
      if (!token) {
        console.log("AuthUtil: Amplify session valid but no ID token JWT found.");
      }
      return token;
    } catch (error) {
      console.log("AuthUtil: Amplify Auth.currentSession failed (likely not logged in):", error);
      return null;
    }
  }
};
var AppAuthScreen = {
  AppAuthScreen: AuthScreen,
  useAuth,
  Auth: Auth$1,
  getCurrentSession,
  getCurrentIdToken,
  getCurrentAuthenticatedUser,
  MFASetup,
  MFAVerification
};

export { AppAuthScreen as A, AuthScreen as a, getCurrentIdToken as g, useAuth as u };
